import { api, UserPlanVo, UserProfile } from "@/api";
import LogoutIcon from "@/assets/icons/logout.svg";
import ManageSubscriptionIcon from "@/assets/icons/ui/manage-subscription.svg";
import ProblemFeedbackIcon from "@/assets/icons/ui/problem-feedback.svg";
import defaultAvatar from "@/assets/images/default-avatar.jpg";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { Button } from "@/component/Button";
import { confirmModal } from "@/component/ConfirmModal";
import { Avatar, Popover } from "antd";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import styles from "../index.module.css";
import DiamondIcon from "./assets/gift-diamond.png";

interface UserPopoverProps {
  user: UserProfile;
  userPlan: UserPlanVo | null;
  isGifted?: boolean;
  feedbackUrl: string;
}

export const UserPopover: React.FC<UserPopoverProps> = ({
  user,
  userPlan,
  isGifted,
  feedbackUrl,
}) => (
  <Popover
    key="user-popover"
    trigger={["hover"]}
    placement="bottom"
    arrow={false}
    classNames={{
      root: "!pr-2 sm:!pr-8 !pt-1",
      body: classNames(
        "!p-2 flex bg-background-card! text-xs text-center !rounded-xl",
        styles.popoverBody,
      ),
    }}
    zIndex={100}
    content={
      <>
        <div className="text-text-primary font-normal p-4 rounded-lg bg-background-elevated w-[240px] sm:w-[260px]">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-start w-[180px] sm:w-[230px]">
              <strong className="text-sm font-medium truncate block">
                {user.nickname}
              </strong>

              <p className="text-xs text-text-secondary truncate block">
                {user.email}
              </p>
            </div>
          </div>

          <div
            className="w-full h-px mt-3 mb-2 opacity-40"
            style={{
              backgroundImage: "url(/dash-line.svg)",
              backgroundRepeat: "repeat-x",
            }}
          ></div>

          <div className="w-full flex items-center justify-between">
            <div className="flex flex-col items-start gap-0.5">
              <div className="flex items-center gap-1">
                <p
                  className={classNames(
                    "font-medium",
                    userPlan?.expireTime ? "text-sm" : "text-base",
                  )}
                >
                  {userPlan?.memberShipName}
                </p>
                {isGifted && (
                  <span
                    className="flex items-center gap-1 px-1.5 h-[22px] rounded-[6px] text-[#F9C9FF] text-[10px] font-bold leading-none"
                    style={{
                      background:
                        "linear-gradient(105deg, #9F418C -11.63%, #401643 34.95%, #401643 51.12%, #5E2351 94.95%)",
                    }}
                  >
                    <Image
                      src={DiamondIcon}
                      alt="Diamond"
                      width={12}
                      height={12}
                    />
                    Gifted
                  </span>
                )}
              </div>

              {userPlan?.expireTime && (
                <p className="text-xs text-text-secondary">
                  Valid until:{" "}
                  {new Date(userPlan.expireTime).toLocaleDateString()}
                </p>
              )}
            </div>

            <Link href="/pricing" data-disable-progress={true}>
              <Button
                type="primary"
                className="!h-7 !px-3 !text-xs"
                onClick={() => {
                  logger.event(VidMuseEvent.HeaderMenuClick, {
                    menu: "user",
                    item: "upgrade",
                  });
                }}
              >
                Upgrade
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-2">
          <Link href="/payment/sessions/portal" target="_blank">
            <Button
              type="plain"
              className="w-full justify-start h-10 rounded-lg pl-4 text-text-primary font-normal hover:bg-background-card-hover"
              icon={<ManageSubscriptionIcon className="w-6 h-6" />}
              onClick={() => {
                logger.event(VidMuseEvent.HeaderMenuClick, {
                  menu: "credit",
                  item: "manage_subscription",
                });
              }}
            >
              Manage Subscription
            </Button>
          </Link>
          <Link
            href="/feedback"
            data-disable-progress={true}
            className="w-full h-10"
          >
            <Button
              type="plain"
              className="w-full h-full justify-start rounded-lg pl-4 text-text-primary hover:bg-background-card-hover"
              icon={<ProblemFeedbackIcon className="w-6 h-6" />}
            >
              Feedback & Issues
            </Button>
          </Link>
          <Button
            type="plain"
            className="w-full justify-start h-10 rounded-lg pl-4 text-text-primary font-normal hover:bg-background-card-hover"
            icon={<LogoutIcon className="w-6 h-6" />}
            onClick={() => {
              logger.event(VidMuseEvent.HeaderMenuClick, {
                menu: "user",
                item: "logout",
              });
              window.location.href = "/auth/logout";
            }}
          >
            Logout
          </Button>
          <div className="h-[0.5px] bg-white opacity-10"></div>
          <Button
            type="plain"
            className="w-full justify-start h-10 rounded-lg pl-4 text-text-secondary font-normal hover:bg-background-card-hover"
            onClick={async () => {
              logger.event(VidMuseEvent.HeaderMenuClick, {
                menu: "user",
                item: "delete_account",
              });

              try {
                await confirmModal.confirm({
                  title: "Delete My Account",
                  content:
                    "This will permanently delete your account and remove your personal data from VidMuse. This action cannot be undone. If you have active subscription plan, please remember to cancel first.",
                  continueText: "Delete my account",
                  backText: "Back",
                  width: 400,
                  backButtonWidth: 120,
                  confirmCountdown: 5,
                });

                await api.user.deactivateUser();
                window.location.href = "/";
              } catch (error) {
                // User cancelled or API failed
                console.error("Failed to deactivate user:", error);
              }
            }}
          >
            Delete My Account
          </Button>
        </div>
      </>
    }
  >
    <Avatar
      src={user.avatarUrl || defaultAvatar.src}
      className="rounded-full cursor-pointer !w-9 !h-9 sm:!w-10 sm:!h-10"
    />
  </Popover>
);
